import { useAuth } from "@/hooks/useAuth";
import { useUserMode } from "@/hooks/useUserMode";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AdaptiveHelp } from "@/components/personalization/AdaptiveHelp";
import SearchForm from "@/components/SearchForm";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import NavigationBar from "@/components/NavigationBar";
import { 
  Search, 
  Calendar, 
  MapPin, 
  Star, 
  TrendingUp,
  Plus,
  ArrowRight,
  Clock,
  Home
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { useDashboardData } from "@/hooks/useDashboardData";
import { format } from "date-fns";
import { de, enUS } from "date-fns/locale";

export const LoggedInHomepage = () => {
  const { user } = useAuth();
  const { mode } = useUserMode();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isHost, upcomingTrips, properties, dashboardStats } = useDashboardData();
  const dateLocale = i18n.language === 'de' ? de : enUS;

  const isHostMode = mode === 'host';

  return (
    <div className="min-h-screen bg-background">
      <AdaptiveHelp />
      <Header />
      <NavigationBar />
      <div className="container mx-auto px-4 py-8 space-y-8">
        {/* Welcome Header */}
        <div className="text-center space-y-4">
          <h1 className="text-3xl font-bold">
            {t('dashboard.welcomeBack')}{user?.user_metadata?.first_name ? `, ${user.user_metadata.first_name}` : ''}!
          </h1>
          <p className="text-muted-foreground">
            {isHostMode && isHost 
              ? t('dashboard.manageProperties')
              : t('dashboard.readyForAdventure')
            }
          </p>
        </div>

        {/* Enhanced Search Bar */}
        {(!isHostMode || !isHost) && (
          <div className="max-w-4xl mx-auto">
            <SearchForm />
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Host Mode Dashboard */}
            {isHostMode && isHost && (
              <>
                {/* KPI Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card>
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-muted-foreground">{t('dashboard.totalRevenue')}</p>
                          <p className="text-2xl font-bold">{dashboardStats.totalRevenue.toFixed(0)}€</p>
                        </div>
                        <div className="p-3 rounded-lg bg-green-100">
                          <TrendingUp className="h-6 w-6 text-green-600" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-muted-foreground">{t('dashboard.occupancy')}</p>
                          <p className="text-2xl font-bold">{dashboardStats.occupancyRate.toFixed(0)}%</p>
                        </div>
                        <div className="p-3 rounded-lg bg-blue-100">
                          <Calendar className="h-6 w-6 text-blue-600" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-muted-foreground">{t('dashboard.requests')}</p>
                          <p className="text-2xl font-bold">{dashboardStats.pendingRequests}</p>
                        </div>
                        <div className="p-3 rounded-lg bg-orange-100">
                          <Clock className="h-6 w-6 text-orange-600" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Properties Overview */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                      <span>{t('dashboard.yourProperties')}</span>
                      <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                        <Button 
                          variant="outline" 
                          onClick={() => navigate("/my-listings")}
                          className="w-full sm:w-auto"
                        >
                          <Home className="h-4 w-4 mr-2" />
                          {t('dashboard.manage')}
                        </Button>
                        <Button 
                          onClick={() => navigate("/create-listing")}
                          className="w-full sm:w-auto"
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          {t('dashboard.newProperty')}
                        </Button>
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {properties.length > 0 ? (
                      <div className="space-y-4">
                        {properties.slice(0, 3).map((property) => (
                          <div key={property.id} className="flex items-center justify-between p-4 border rounded-lg">
                            <div className="flex-1">
                              <h3 className="font-semibold">{property.title}</h3>
                              <p className="text-sm text-muted-foreground">{property.city}</p>
                            </div>
                            <div className="text-right">
                              <p className="font-semibold">{property.revenue.toFixed(0)}€</p>
                              <p className="text-sm text-muted-foreground">{property.occupancyRate.toFixed(0)}% {t('dashboard.occupancy')}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <Home className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                        <h3 className="font-semibold mb-2">{t('dashboard.noProperties')}</h3>
                        <p className="text-muted-foreground mb-4">{t('dashboard.createFirstListing')}</p>
                        <Button onClick={() => navigate("/create-listing")}>
                          <Plus className="h-4 w-4 mr-2" />
                          {t('dashboard.createFirstProperty')}
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            )}

            {/* Guest Mode - Upcoming Trips */}
            {(!isHostMode || !isHost) && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>{t('dashboard.yourTrips')}</span>
                    <Button variant="outline" onClick={() => navigate("/bookings")}>
                      {t('common.showAll')}
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {upcomingTrips.length > 0 ? (
                    <div className="space-y-4">
                      {upcomingTrips.slice(0, 2).map((trip) => (
                        <div key={trip.id} className="flex items-center justify-between p-4 border rounded-lg">
                          <div className="flex-1">
                            <h3 className="font-semibold">{trip.listingTitle}</h3>
                            <div className="flex items-center text-sm text-muted-foreground mt-1">
                              <MapPin className="h-3 w-3 mr-1" />
                              {trip.listingCity}
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold">
                              {format(new Date(trip.checkInDate), 'dd. MMM', { locale: dateLocale })}
                            </p>
                            <Badge variant="outline">
                              {trip.status === 'confirmed' ? t('dashboard.confirmed') : t('dashboard.pending')}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                      <h3 className="font-semibold mb-2">{t('dashboard.noTrips')}</h3>
                      <p className="text-muted-foreground mb-4">{t('dashboard.discoverListings')}</p>
                      <Button onClick={() => navigate("/search")}>
                        <Search className="h-4 w-4 mr-2" />
                        {t('dashboard.searchNow')}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle>{t('dashboard.quickAccess')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {isHostMode && isHost ? (
                  <>
                    <Button variant="outline" className="w-full justify-start" onClick={() => navigate("/my-listings")}>
                      <Home className="h-4 w-4 mr-2" />
                      {t('dashboard.myProperties')}
                    </Button>
                    <Button variant="outline" className="w-full justify-start" onClick={() => navigate("/create-listing")}>
                      <Plus className="h-4 w-4 mr-2" />
                      {t('dashboard.newProperty')}
                    </Button>
                    <Button variant="outline" className="w-full justify-start" onClick={() => navigate("/host-dashboard")}>
                      <TrendingUp className="h-4 w-4 mr-2" />
                      {t('dashboard.analytics')}
                    </Button>
                    <Button variant="outline" className="w-full justify-start" onClick={() => navigate("/bookings")}>
                      <Calendar className="h-4 w-4 mr-2" />
                      {t('dashboard.bookings')}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant="outline" className="w-full justify-start" onClick={() => navigate("/search")}>
                      <Search className="h-4 w-4 mr-2" />
                      {t('dashboard.searchListings')}
                    </Button>
                    <Button variant="outline" className="w-full justify-start" onClick={() => navigate("/bookings")}>
                      <Calendar className="h-4 w-4 mr-2" />
                      {t('dashboard.myBookings')}
                    </Button>
                    <Button variant="outline" className="w-full justify-start" onClick={() => navigate("/host-services")}>
                      <TrendingUp className="h-4 w-4 mr-2" />
                      {t('dashboard.becomeHost')}
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Recommendations */}
            <Card>
              <CardHeader>
                <CardTitle>{t('dashboard.recommendations')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="p-3 border rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
                     onClick={() => navigate("/search?destination=Berlin")}>
                  <h4 className="font-semibold text-sm">{t('dashboard.discoverBerlin')}</h4>
                  <p className="text-xs text-muted-foreground">{t('dashboard.over200Listings')}</p>
                  <Badge variant="outline" className="mt-1">{t('dashboard.fromPerNight', { price: '45' })}</Badge>
                </div>
                <div className="p-3 border rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
                     onClick={() => navigate("/search?destination=München")}>
                  <h4 className="font-semibold text-sm">{t('dashboard.discoverMunich')}</h4>
                  <p className="text-xs text-muted-foreground">{t('dashboard.discoverBavaria')}</p>
                  <Badge variant="outline" className="mt-1">{t('dashboard.fromPerNight', { price: '65' })}</Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};
